/**
 * Nombre visible de una persona en la interfaz. Espejo exacto de
 * `packages/backend/src/domain/users/userDisplayName.ts`.
 *
 * Existe porque el frontend tenía SEIS versiones distintas de lo mismo, y una con el orden invertido
 * (`firstName + lastName` ANTES que `fullName`), así que la misma persona podía leerse de dos maneras
 * según la pantalla. El backend ya se unificó; esto cierra el otro lado.
 *
 * TRES ESCALONES, el mismo orden que el backend:
 *   1. `fullName` — el nombre tal como se capturó al dar de alta al usuario.
 *   2. `firstName + lastName` — reconstruido, para quien tenga las partes y no el compuesto.
 *   3. `username` — último recurso, para no dejar el hueco vacío.
 *
 * Acepta cualquier objeto con esos campos (AuthUser, User, o el resumen que mande la API) y también
 * `null`, para no obligar a cada pantalla a decidir qué hacer con un usuario ausente.
 */
export interface NameableUser {
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}

export function userDisplayName(user: NameableUser | null | undefined): string | null {
  if (!user) return null;
  const composed = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return user.fullName?.trim() || composed || user.username || null;
}

/**
 * Igual que `userDisplayName`, pero nunca devuelve vacío. Para los sitios donde el hueco quedaría
 * feo y no hay nada útil que decir — la barra superior, el origen de un movimiento —, a diferencia
 * de los documentos, donde el vacío SÍ significa algo y se dice «Sin dato».
 */
export function userDisplayNameOr(user: NameableUser | null | undefined, fallback = 'Usuario'): string {
  return userDisplayName(user) ?? fallback;
}
